'use client'

import { createContext, useContext } from 'react'

import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'

import type { CardVariant } from './theme-tokens'

export type { CardVariant } from './theme-tokens'

type CardContextType = {
  variant: CardVariant
  showTrophyRow: boolean
}

interface CardProviderProps {
  variant?: CardVariant
  showTrophies?: boolean
  children: React.ReactNode
}

export const CardContext = createContext<CardContextType | undefined>(undefined)

export const useCardContext = () => {
  const context = useContext(CardContext)
  if (!context) {
    throw new Error('useCardContext must be used within a CardProvider')
  }
  return context
}

export const CardProvider = ({
  variant = 'default',
  showTrophies = false,
  children
}: CardProviderProps) => {
  const showTrophyRow = showTrophies
  return (
    <CardContext.Provider value={{ variant, showTrophyRow }}>
      {children}
    </CardContext.Provider>
  )
}

// ---- Variants ----

const cardVariantStyles = {
  default:
    'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#D5D2FF_447.79%)] shadow-[0_8px_0px_var(--color-primary),0_4px_25px_rgba(136,0,134,0.8),inset_0_4px_0px_#FDF5FF]',
  purple:
    'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#E8D4F4_447.79%)] shadow-[0_8px_0px_#5c1a7a,0_4px_25px_rgba(120,34,159,0.5),inset_0_4px_0px_#FDF5FF]',
  yellow:
    'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#F5ECD4_447.79%)] shadow-[0_8px_0px_var(--color-primary),0_4px_25px_rgba(136,0,134,0.8),inset_0_4px_0px_#FDF5FF]',
  lightBlue:
    'bg-[linear-gradient(0deg,#FBFBFB_35.29%,#D4E8F7_447.79%)] shadow-[0_8px_0px_#5a9fc9,0_4px_25px_rgba(90,159,201,0.4),inset_0_4px_0px_#F5FBFF]',
  gray: '',
  dark: 'bg-[#1D1D2A] shadow-[0_8px_0px_#000,0_-4px_0px_#182A62,0_4px_25px_rgba(136,0,134,0.8)] text-white'
}

export const cardVariants = cva(
  'rounded-xl my-[20px] max-w-2xl w-full px-5 relative flex flex-col',
  {
    variants: {
      variant: cardVariantStyles
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

export const cardHeaderVariants = cva(
  'shrink-0 flex flex-col justify-between items-center pt-3 px-3 rounded-3xl mt-[-20px]',
  {
    variants: {
      variant: {
        default: 'bg-[#f6f6fb]',
        purple: 'bg-[#f6f0fa]',
        yellow: 'bg-[#f6f6fb]',
        lightBlue: 'bg-[#f0f7fc]',
        gray: '',
        dark: 'bg-[#1D1D2A] text-white shadow-[0_-4px_0px_#182A62]'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

export const cardHeaderInnerVariants = cva('w-full rounded-3xl p-3', {
  variants: {
    variant: {
      default:
        'bg-white bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#BAC4F0_143.3%)] shadow-[0_5px_0_-1px_#6A81DB,inset_0_4px_0_0_rgba(255,255,255,0.25)]',
      purple:
        'bg-white bg-[linear-gradient(360deg,_#F0E6F8_-12.43%,_#D4B8E8_143.3%)] shadow-[0_5px_0_-1px_#78229f,inset_0_4px_0_rgba(255,255,255,0.25)]',
      yellow:
        'bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#F0DFBA_143.3%)] shadow-[0_5px_0_-1px_#F39D72,inset_0_2px_0_0_rgba(255,255,255,0.25)]',
      lightBlue:
        'bg-white bg-[linear-gradient(360deg,_#E8F4FC_-12.43%,_#B8D7F0_143.3%)] shadow-[0_5px_0_-1px_#7eb8e0,inset_0_4px_0_rgba(255,255,255,0.25)]',
      gray: '',
      dark: 'bg-gradient-to-b from-[#434368] to-[#434368] shadow-[0_5px_0_-1px_#182A62,inset_0_4px_0_0_#72728E]',
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

export const cardTitleVariants = cva(
  'block m-0 font-sans font-bold text-center',
  {
    variants: {
      variant: {
        default: 'text-primary-dark',
        purple: 'text-[#4a1560]',
        yellow: 'text-[#974319]',
        lightBlue: 'text-[#2a6b8f]',
        gray: '',
        dark: 'text-white'
      }
    },
    defaultVariants: { variant: 'default' }
  }
)

export const cardBodyVariants = cva('space-y-4 py-5', {
  variants: {
    variant: {
      default: '',
      purple: '',
      yellow: '',
      lightBlue: '',
      gray: '',
      dark: ''
    }
  },
  defaultVariants: { variant: 'default' }
})

export const cardFooterVariants = cva(
  'shrink-0 grow-0 flex items-center justify-between gap-6 w-[90%] md:w-[80%] mx-auto px-3 pb-3  rounded-2xl mb-[-30px] z-2',
  {
    variants: {
      variant: {
        default: 'bg-white shadow-[0_8px_0_-1px_var(--color-primary)]',
        purple: 'bg-white shadow-[0_8px_0_-1px_#78229f]',
        yellow: 'bg-white shadow-[0_8px_0_-1px_#F39D72]',
        lightBlue: 'bg-white shadow-[0_8px_0_-1px_#5a9fc9]',
        gray: '',
        dark: 'bg-[#1D1D2A] text-white shadow-[0_8px_0_-1px_#000]'
      }
    },
    defaultVariants: { variant: 'default' }
  }
)

export const cardTileVariants = cva('rounded-md p-2 flex gap-2 items-center', {
  variants: {
    variant: {
      default: 'bg-[linear-gradient(0deg,#E4E8FA_0%,_#DDD4FF_152.91%)]',
      purple: 'bg-[linear-gradient(0deg,#E8D4F4_0%,_#E0C8F0_152.91%)]',
      yellow: 'bg-[linear-gradient(0deg,#F5ECD4_0%,_#F0DFBA_152.91%)]',
      lightBlue: 'bg-[linear-gradient(0deg,#D4E8F7_0%,_#C8E0F4_152.91%)]',
      gray: '',
      dark: 'bg-[#292943] text-white'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

export const cardTileImageVariants = cva('w-16 h-16 rounded-md flex-shrink-0', {
  variants: {
    variant: {
      default: 'bg-white',
      purple: 'bg-white',
      yellow: 'bg-white',
      lightBlue: 'bg-white',
      gray: '',
      dark: ''
    }
  },
  defaultVariants: { variant: 'default' }
})

export const cardRewardsVariants = cva(
  // `display` mora nas variantes de layout, não na base: o cva do projeto não
  // resolve conflito de classe, então `grid` e `flex` juntos seriam indefinidos.
  'min-w-13 pt-1 pb-1 ps-2 pe-3 rounded-md relative',
  {
    variants: {
      layout: {
        grid: 'h-13 grid place-items-center',
        inline: 'h-11 flex items-center gap-2'
      },
      multiple: {
        true: '',
        false: ''
      },
      gradient: {
        true: 'text-white',
        false: ''
      },
      completed: {
        true: '',
        false: ''
      },
      claimed: {
        true: 'bg-[#31619E] shadow-[0_-3px_0_0_#242476] text-white',
        false: ''
      }
    },
    compoundVariants: [
      {
        layout: 'grid',
        multiple: true,
        class: 'grid-cols-2 grid-rows-2'
      },
      {
        layout: 'grid',
        multiple: false,
        class: 'grid-cols-1 grid-rows-2'
      },
      {
        gradient: true,
        completed: true,
        class: 'bg-white/20'
      },
      {
        gradient: true,
        claimed: true,
        class: 'bg-white/30'
      },
      {
        gradient: false,
        claimed: false,
        completed: false,
        class: 'inset-shadow-[0_4px_4px_0_#cacaca] text-[#7B7B7B]'
      },
      {
        gradient: false,
        completed: true,
        claimed: false,
        class:
          'bg-gradient-to-b from-[#3F80CA] to-[#31619E] shadow-[0_3px_0_0px_#242476,inset_0_2px_0_0_#35B1FF] text-white'
      }
    ],
    defaultVariants: {
      layout: 'grid',
      multiple: false,
      gradient: false,
      completed: false,
      claimed: false
    }
  }
)

export function Card({
  variant = 'default',
  showTrophies,
  className = '',
  children,
  id
}: {
  variant?: CardVariant
  showTrophies?: boolean
  className?: string
  children: React.ReactNode
  id?: string
}) {
  const showTrophyRow = showTrophies === true
  return (
    <CardProvider variant={variant} showTrophies={showTrophies}>
      <div
        className={cn(
          cardVariants({ variant, className }),
          showTrophyRow && 'mt-[80px] lg:mt-[100px]'
        )}
        id={id}
      >
        {children}
      </div>
    </CardProvider>
  )
}

export function CardHeader({
  className = '',
  children
}: {
  className?: string
  children: React.ReactNode
}) {
  const { variant, showTrophyRow } = useCardContext()

  return (
    <div
      className={cn(
        cardHeaderVariants({ variant, className }),
        showTrophyRow && 'p-0 rounded-none mt-0 bg-transparent'
      )}
    >
      {showTrophyRow && (
        <div className="flex w-full items-end justify-center -mt-20 lg:-mt-25">
          <div className="size-25 lg:size-30 bg-[#f6f6fb] rounded-l-3xl p-4 -mb-1">
            <img
              src="/misc/trophy-silver-squared.svg"
              alt="Medal"
              className="size-full"
            />
          </div>
          <div className="size-30 lg:size-35 bg-[#f6f6fb] rounded-3xl p-4 -mb-1">
            <img
              src="/misc/trophy-gold-squared.svg"
              alt="Medal"
              className="size-full"
            />
          </div>
          <div className="size-25 lg:size-30 bg-[#f6f6fb] rounded-r-3xl p-4 -mb-1">
            <img
              src="/misc/trophy-bronze-squared.svg"
              alt="Medal"
              className="size-full"
            />
          </div>
        </div>
      )}
      <div className={cardHeaderInnerVariants({ variant })}>{children}</div>
    </div>
  )
}

export function CardTitle({
  className = '',
  children,
  onClick
}: {
  className?: string
  children: React.ReactNode
  onClick?: () => void
}) {
  const { variant } = useCardContext()

  return (
    <span
      className={cardTitleVariants({ variant, className })}
      onClick={onClick}
    >
      {children}
    </span>
  )
}

export function CardBody({
  className = '',
  children
}: {
  className?: string
  children: React.ReactNode
}) {
  const { variant } = useCardContext()
  return (
    <div className={cardBodyVariants({ variant, className })}>{children}</div>
  )
}

export function CardFooter({
  className = '',
  children
}: {
  className?: string
  children: React.ReactNode
}) {
  const { variant } = useCardContext()
  return (
    <div className={cardFooterVariants({ variant, className })}>{children}</div>
  )
}

export const CardTile = ({
  className = '',
  children,
  onClick
}: {
  className?: string
  children: React.ReactNode
  onClick?: () => void
}) => {
  const { variant } = useCardContext()

  return (
    <div className={cardTileVariants({ variant, className })} onClick={onClick}>
      {children}
    </div>
  )
}

export const CardTileImage = ({
  src,
  className,
  width = 68,
  height = 68,
  alt = ''
}: {
  src: string
  className?: string
  width?: number
  height?: number
  alt?: string
}) => {
  const { variant } = useCardContext()

  if (!src) return null
  return (
    <img
      src={src}
      alt={alt}
      className={cardTileImageVariants({ variant, className })}
      width={width}
      height={height}
    />
  )
}

interface CardRewardsProps extends React.HTMLAttributes<HTMLDivElement> {
  coinReward: number
  pointReward: number
  /**
   * Pontos que o aluno já conquistou no escopo. Quando informado, o slot de
   * pontos vira o placar de aproveitamento (`150/300`) em vez do total seco —
   * é o que mostra ao aluno o que ficou pelo caminho em respostas erradas.
   */
  acquiredPointReward?: number
  /**
   * `grid` (padrão) empilha pontos e moedas em duas linhas; `inline` põe tudo
   * numa linha só — cabe melhor quando o slot de pontos é uma fração larga.
   */
  layout?: 'grid' | 'inline'
  completed: boolean
  claimed: boolean
  gradient?: boolean
  withClaimedBadge?: boolean
}

export const CardRewards = ({
  coinReward,
  pointReward,
  acquiredPointReward,
  layout = 'grid',
  completed,
  claimed,
  gradient = false,
  withClaimedBadge = true,
  className = '',
  ...props
}: CardRewardsProps) => {
  const multiple = !!coinReward && !!pointReward
  const showScoreboard = acquiredPointReward !== undefined
  const total = Math.round(pointReward)
  const acquired = Math.round(acquiredPointReward ?? 0)

  if (!coinReward && !pointReward) return null

  const inline = layout === 'inline'

  const classes = cardRewardsVariants({
    layout,
    multiple,
    gradient,
    completed,
    claimed,
    className
  })

  // No grid o span ocupa a célula inteira; inline ele acompanha o próprio texto,
  // senão a fração empurra a moeda para fora da linha.
  const valueClassName =
    'select-none text-md font-sans ' +
    (inline
      ? 'whitespace-nowrap'
      : 'block w-full ' + (multiple ? 'text-left' : 'text-center'))

  return (
    <div className={classes} {...props}>
      {!!pointReward && (
        <>
          <img
            src="/icons/star.svg"
            alt=""
            className={
              multiple
                ? 'size-5 select-none'
                : 'size-6 select-none' + (!completed ? ' grayscale' : '')
            }
          />
          <span
            className={valueClassName}
            aria-label={
              showScoreboard
                ? `Aproveitamento de pontos: ${acquired} de ${total}`
                : undefined
            }
          >
            {showScoreboard ? (
              <>
                {acquired}
                <span className="opacity-70">/{total}</span>
              </>
            ) : (
              pointReward
            )}
          </span>
        </>
      )}
      {!!coinReward && (
        <>
          <img
            src="/icons/coin.svg"
            alt=""
            className={
              multiple
                ? 'size-5 select-none'
                : 'size-6 select-none' + (!completed ? ' grayscale' : '')
            }
          />
          <span className={valueClassName}>{coinReward}</span>
        </>
      )}
      {withClaimedBadge && claimed && (
        // O selo sobrepõe a borda da própria caixa de pontuação: à esquerda no
        // grid (layout histórico), à direita no inline, onde o canto esquerdo
        // é ocupado pelo ícone de pontos.
        <div
          className={
            inline
              ? 'absolute top-0 right-0 size-8 translate-x-1/2 -translate-y-1/2'
              : 'absolute top-1 left-1 size-12 -translate-x-1/2 -translate-y-1/2'
          }
        >
          <img
            src="/icons/completed-light.svg"
            alt="Concluído"
            className="size-full"
          />
        </div>
      )}
    </div>
  )
}
